import { useEffect, useMemo, useState } from 'react';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import {
  Alert, Box, Button, Card, CardContent, Chip, CircularProgress, Dialog, DialogActions, DialogContent,
  DialogTitle, FormControl, FormControlLabel, Grid, IconButton, InputLabel, MenuItem, Paper, Select,
  Stack, Switch, TextField, Typography, Tooltip,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import FilterListIcon from '@mui/icons-material/FilterList';
import {
  listFees, createFee, updateFee, deleteFee, listTenants, listSindicatos,
} from '../api/services';
import type { Fee, Sindicato } from '../../types/domain';
import { fmtCurrency, FEE_CODE, FEE_AGREEMENT_LABEL } from '../brand';
import { useAppDispatch, useAppSelector } from '../store/store';
import { showMessage } from '../store/uiSlice';
import PageContainer from '../components/PageContainer';
import { ListCardField } from '../components/ListCardField';
import { useIsXs } from '../hooks/useMobileLayout';

interface FormState {
  feeId?: number;
  tenantId: number | null;
  /** null/undefined = toda la organización; número = tarifa por registro CAMARAS */
  sindicatoId: number | null;
  code: 'AGREEMENT' | 'NO_AGREEMENT';
  name: string;
  documentType: 'DNI' | 'PASSPORT';
  amount: number;
  currency: 'ARS' | 'USD';
  description: string;
  validFrom: Date;
  validTo: Date | null;
  active: boolean;
}

const blank: FormState = {
  tenantId: null,
  sindicatoId: null,
  code: 'AGREEMENT',
  name: '',
  documentType: 'DNI',
  amount: 0,
  currency: 'ARS',
  description: '',
  validFrom: new Date(),
  validTo: null,
  active: true,
};

export default function AdminFees() {
  const isXs = useIsXs();
  const dispatch = useAppDispatch();
  const user = useAppSelector((s) => s.auth.user);
  const isSuperAdmin = !!user?.isSuperAdmin;

  const [fees, setFees] = useState<Fee[]>([]);
  const [camarasAll, setCamarasAll] = useState<Sindicato[]>([]);
  const [orgPick, setOrgPick] = useState<{ tenantId: number; name: string }[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  /** SuperAdmin: filtrar listado por CAMARAS (se deriva tenant). null = todas las tarifas. */
  const [filterCamaraScopeId, setFilterCamaraScopeId] = useState<number | null>(null);

  const filterTenant = useMemo(() => {
    if (!isSuperAdmin) return user?.tenantId ?? null;
    if (filterCamaraScopeId == null) return null;
    return camarasAll.find((c) => c.sindicatoId === filterCamaraScopeId)?.tenantId ?? null;
  }, [isSuperAdmin, user?.tenantId, filterCamaraScopeId, camarasAll]);
  const [includeGlobal, setIncludeGlobal] = useState(true);
  /** Filtro de listado: todas | solo nivel organización | un registro CAMARAS */
  const [filterSindicato, setFilterSindicato] = useState<'all' | 'empresa' | number>('all');
  const [sindicatosFilter, setSindicatosFilter] = useState<Sindicato[]>([]);
  const [sindicatosForm, setSindicatosForm] = useState<Sindicato[]>([]);
  const [open, setOpen] = useState(false);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [form, setForm] = useState<FormState>(blank);
  const [busy, setBusy] = useState(false);

  async function load() {
    setLoading(true);
    setError(null);
    try {
      const sidParam = filterSindicato === 'all' ? undefined : filterSindicato;
      if (isSuperAdmin) {
        const [feeRes, sRes, tRes] = await Promise.all([
          listFees({ tenantId: filterTenant, includeGlobal, sindicatoId: sidParam as any }),
          listSindicatos({ includeInactive: true }),
          listTenants({ includeInactive: false }),
        ]);
        setFees(feeRes.fees);
        setCamarasAll(sRes.sindicatos);
        const m = new Map<number, string>();
        tRes.tenants.forEach((t) => m.set(t.tenantId, t.name));
        sRes.sindicatos.forEach((c) => {
          if (!m.has(c.tenantId)) m.set(c.tenantId, c.tenantName || `Organización ·${c.tenantId}`);
        });
        setOrgPick([...m.entries()].map(([tenantId, name]) => ({ tenantId, name })));
      } else {
        const feeRes = await listFees({ tenantId: filterTenant, includeGlobal, sindicatoId: sidParam as any });
        setFees(feeRes.fees);
      }
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Error al cargar tarifas');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (isSuperAdmin && filterTenant == null) setFilterSindicato('all');
  }, [filterTenant, isSuperAdmin]);

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filterTenant, includeGlobal, filterSindicato]);

  useEffect(() => {
    (async () => {
      if (isSuperAdmin && filterTenant == null && camarasAll.length) {
        setSindicatosFilter(camarasAll);
        return;
      }
      if (filterTenant == null && !isSuperAdmin) {
        setSindicatosFilter([]);
        return;
      }
      const tid = isSuperAdmin ? filterTenant : user?.tenantId;
      if (tid == null) {
        setSindicatosFilter([]);
        return;
      }
      try {
        const res = await listSindicatos({ tenantId: tid, includeInactive: true });
        setSindicatosFilter(res.sindicatos);
      } catch {
        setSindicatosFilter([]);
      }
    })();
  }, [filterTenant, isSuperAdmin, user?.tenantId, camarasAll]);

  useEffect(() => {
    (async () => {
      const tid = form.tenantId;
      if (!open || tid == null) {
        setSindicatosForm([]);
        return;
      }
      try {
        const res = await listSindicatos({ tenantId: tid, includeInactive: true });
        setSindicatosForm(res.sindicatos);
      } catch {
        setSindicatosForm([]);
      }
    })();
  }, [form.tenantId, open]);

  function startCreate() {
    setForm({
      ...blank,
      tenantId: isSuperAdmin ? (filterTenant ?? orgPick[0]?.tenantId ?? null) : (user?.tenantId ?? null),
      sindicatoId: null,
    });
    setOpen(true);
  }

  function startEdit(fee: Fee) {
    setForm({
      feeId: fee.feeId,
      tenantId: fee.tenantId,
      sindicatoId: fee.sindicatoId ?? null,
      code: fee.code,
      name: fee.name,
      documentType: fee.documentType,
      amount: Number(fee.amount),
      currency: fee.currency,
      description: fee.description || '',
      validFrom: new Date(fee.validFrom),
      validTo: fee.validTo ? new Date(fee.validTo) : null,
      active: fee.active,
    });
    setOpen(true);
  }

  async function handleSave() {
    setBusy(true);
    try {
      const payload = {
        tenantId: form.tenantId,
        sindicatoId: form.tenantId == null ? null : (form.sindicatoId != null ? form.sindicatoId : null),
        code: form.code,
        name: form.name,
        documentType: form.documentType,
        amount: Number(form.amount),
        currency: form.currency,
        description: form.description || undefined,
        validFrom: form.validFrom?.toISOString(),
        validTo: form.validTo ? form.validTo.toISOString() : null,
        active: form.active,
      };
      if (form.feeId) await updateFee(form.feeId, payload as any);
      else await createFee(payload as any);
      dispatch(showMessage({ message: form.feeId ? 'Tarifa actualizada' : 'Tarifa creada', severity: 'success' }));
      setOpen(false);
      load();
    } catch (err: any) {
      dispatch(showMessage({ message: err?.response?.data?.message || 'No se pudo guardar', severity: 'error' }));
    } finally {
      setBusy(false);
    }
  }

  async function handleDelete(fee: Fee) {
    if (!window.confirm(`¿Desactivar tarifa "${fee.name}"?`)) return;
    try {
      await deleteFee(fee.feeId);
      dispatch(showMessage({ message: 'Tarifa desactivada', severity: 'success' }));
      load();
    } catch (err: any) {
      dispatch(showMessage({ message: err?.response?.data?.message || 'Error al borrar', severity: 'error' }));
    }
  }

  return (
    <PageContainer>
      <Stack direction={{ xs: 'column', sm: 'row' }} alignItems={{ sm: 'center' }} justifyContent="space-between" gap={2} sx={{ mb: 2 }}>
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="h5" component="h1" sx={{ fontWeight: 700 }}>Tarifas</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ display: { xs: 'none', sm: 'block' } }}>
            Configurá los importes de Acuerdo y Sin Acuerdo por organización y CAMARAS; el sistema usa la global como fallback.
          </Typography>
        </Box>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ width: { xs: 1, sm: 'auto' } }}>
          <Button
            variant="outlined"
            color="primary"
            startIcon={<FilterListIcon />}
            onClick={() => setFiltersOpen(true)}
            fullWidth={isXs}
            sx={{ fontWeight: 700, textTransform: 'none' }}
          >
            Filtros
          </Button>
          <Button variant="contained" startIcon={<AddIcon />} onClick={startCreate} fullWidth={isXs} sx={{ flexShrink: 0 }}>
            Nueva tarifa
          </Button>
        </Stack>
      </Stack>

      <Dialog open={filtersOpen} onClose={() => setFiltersOpen(false)} maxWidth="sm" fullWidth fullScreen={isXs} scroll="body">
        <DialogTitle sx={{ fontWeight: 800 }}>Filtros</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 0.5 }}>
            {isSuperAdmin && (
              <FormControl size="small" fullWidth>
                <InputLabel>CAMARAS (alcance)</InputLabel>
                <Select
                  label="CAMARAS (alcance)"
                  value={filterCamaraScopeId ?? ''}
                  onChange={(e) => setFilterCamaraScopeId(e.target.value === '' ? null : Number(e.target.value))}
                >
                  <MenuItem value="">Todas</MenuItem>
                  {camarasAll.map((c) => (
                    <MenuItem key={c.sindicatoId} value={c.sindicatoId}>
                      {c.name}{c.tenantName ? ` — ${c.tenantName}` : ''}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
            {(filterTenant != null || !isSuperAdmin) && (
              <FormControl size="small" fullWidth>
                <InputLabel>Ámbito</InputLabel>
                <Select
                  label="Ámbito"
                  value={filterSindicato === 'all' ? 'all' : filterSindicato === 'empresa' ? 'empresa' : String(filterSindicato)}
                  onChange={(e) => {
                    const v = e.target.value;
                    if (v === 'all') setFilterSindicato('all');
                    else if (v === 'empresa') setFilterSindicato('empresa');
                    else setFilterSindicato(Number(v));
                  }}
                >
                  <MenuItem value="all">Todas (organización + CAMARAS)</MenuItem>
                  <MenuItem value="empresa">Solo nivel organización</MenuItem>
                  {sindicatosFilter.filter((s) => s.active).map((s) => (
                    <MenuItem key={s.sindicatoId} value={String(s.sindicatoId)}>CAMARAS: {s.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
            <FormControlLabel control={<Switch checked={includeGlobal} onChange={(e) => setIncludeGlobal(e.target.checked)} />} label="Incluir tarifas globales" />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button variant="contained" onClick={() => setFiltersOpen(false)} fullWidth={isXs}>Listo</Button>
        </DialogActions>
      </Dialog>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Paper sx={{ overflow: 'hidden', p: { xs: 1.5, sm: 2 } }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>
        ) : fees.length ? (
          <Grid container spacing={2}>
            {fees.map((f) => (
              <Grid key={f.feeId} size={{ xs: 12, sm: 6, lg: 4 }}>
                <Card variant="outlined" sx={{ height: '100%', borderRadius: 2, boxShadow: '0 1px 6px rgba(0,0,0,0.06)' }}>
                  <CardContent sx={{ display: 'flex', flexDirection: 'column', gap: 1.25, '&:last-child': { pb: 2 } }}>
                    <Stack direction="row" justifyContent="space-between" alignItems="flex-start" gap={1}>
                      <Box sx={{ minWidth: 0, flex: 1 }}>
                        <Typography variant="h6" sx={{ fontWeight: 800 }}>{fmtCurrency(f.amount, f.currency)}</Typography>
                        <Typography variant="subtitle2" sx={{ fontWeight: 700, mt: 0.5 }}>{f.name}</Typography>
                      </Box>
                      <Stack direction="row">
                        <Tooltip title="Editar"><IconButton onClick={() => startEdit(f)} size="small"><EditIcon fontSize="small" /></IconButton></Tooltip>
                        <Tooltip title="Desactivar"><IconButton onClick={() => handleDelete(f)} color="error" size="small"><DeleteIcon fontSize="small" /></IconButton></Tooltip>
                      </Stack>
                    </Stack>
                    <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                      <Chip size="small" color={f.active ? 'success' : 'default'} label={f.active ? 'Activa' : 'Inactiva'} />
                      {f.tenantId == null && <Chip size="small" label="Global" color="warning" />}
                    </Stack>
                    <ListCardField label="Organización">
                      <Typography variant="body2">
                        {f.tenantId == null ? '—' : (camarasAll.find((c) => c.tenantId === f.tenantId)?.tenantName || orgPick.find((o) => o.tenantId === f.tenantId)?.name || `#${f.tenantId}`)}
                      </Typography>
                    </ListCardField>
                    <ListCardField label="Ámbito">
                      <Box>
                        {f.tenantId == null ? (
                          <Typography variant="body2">—</Typography>
                        ) : f.sindicatoId == null ? (
                          <Chip size="small" variant="outlined" label="Toda la organización" />
                        ) : (
                          <Chip size="small" color="info" label={f.sindicatoName || `CAMARAS #${f.sindicatoId}`} />
                        )}
                      </Box>
                    </ListCardField>
                    <ListCardField label="Código">
                      <Typography variant="body2">{f.code === FEE_CODE.AGREEMENT ? 'Acuerdo' : 'Sin Acuerdo'}</Typography>
                    </ListCardField>
                    <ListCardField label="Documento">
                      <Typography variant="body2">{f.documentType === 'DNI' ? 'DNI' : 'Pasaporte'}</Typography>
                    </ListCardField>
                    <ListCardField label="Vigencia">
                      <Typography variant="body2" component="div">desde {new Date(f.validFrom).toLocaleString('es-AR')}</Typography>
                      <Typography variant="body2" component="div" color="text.secondary">{f.validTo ? `hasta ${new Date(f.validTo).toLocaleString('es-AR')}` : 'sin vencimiento'}</Typography>
                    </ListCardField>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        ) : (
          <Typography variant="body2" color="text.secondary" sx={{ py: 3, textAlign: 'center' }}>No hay tarifas para mostrar.</Typography>
        )}
      </Paper>

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth fullScreen={isXs} scroll="paper">
        <DialogTitle>{form.feeId ? 'Editar tarifa' : 'Nueva tarifa'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            {isSuperAdmin && (
              <FormControl size="small" fullWidth>
                <InputLabel>Organización</InputLabel>
                <Select
                  label="Organización"
                  value={form.tenantId ?? ''}
                  onChange={(e) => {
                    const v = e.target.value === '' ? null : Number(e.target.value);
                    setForm((f) => ({ ...f, tenantId: v, sindicatoId: v == null ? null : f.sindicatoId }));
                  }}
                  disabled={!!form.feeId}
                >
                  <MenuItem value="">Tarifa global (fallback)</MenuItem>
                  {orgPick.map((o) => (
                    <MenuItem key={o.tenantId} value={o.tenantId}>{o.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
            {form.tenantId != null && (
              <FormControl size="small" fullWidth>
                <InputLabel>Ámbito tarifa</InputLabel>
                <Select
                  label="Ámbito tarifa"
                  value={form.sindicatoId != null && form.sindicatoId !== 0 ? String(form.sindicatoId) : ''}
                  onChange={(e) => setForm((f) => ({
                    ...f,
                    sindicatoId: e.target.value === '' ? null : Number(e.target.value),
                  }))}
                  disabled={!!form.feeId}
                >
                  <MenuItem value="">Toda la organización (vigencia general)</MenuItem>
                  {sindicatosForm.filter((s) => s.active).map((s) => (
                    <MenuItem key={s.sindicatoId} value={String(s.sindicatoId)}>CAMARAS: {s.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
            <FormControl size="small" fullWidth>
              <InputLabel>Tipo</InputLabel>
              <Select
                label="Tipo"
                value={form.code}
                onChange={(e) => setForm((f) => ({ ...f, code: e.target.value as any, documentType: e.target.value === 'AGREEMENT' ? 'DNI' : 'PASSPORT', currency: e.target.value === 'AGREEMENT' ? 'ARS' : 'USD' }))}
                disabled={!!form.feeId}
              >
                <MenuItem value="AGREEMENT">{FEE_AGREEMENT_LABEL} (DNI / ARS)</MenuItem>
                <MenuItem value="NO_AGREEMENT">Sin Acuerdo (Pasaporte / USD)</MenuItem>
              </Select>
            </FormControl>
            <TextField label="Nombre visible" value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} fullWidth size="small" required />
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField label="Importe" type="number" value={form.amount} onChange={(e) => setForm((f) => ({ ...f, amount: Number(e.target.value) }))} fullWidth size="small" required inputProps={{ min: 0, step: 0.01 }} />
              <FormControl size="small" fullWidth sx={{ minWidth: { sm: 120 } }}>
                <InputLabel>Moneda</InputLabel>
                <Select label="Moneda" value={form.currency} onChange={(e) => setForm((f) => ({ ...f, currency: e.target.value as any }))}>
                  <MenuItem value="ARS">ARS</MenuItem>
                  <MenuItem value="USD">USD</MenuItem>
                </Select>
              </FormControl>
              <FormControl size="small" fullWidth sx={{ minWidth: { sm: 140 } }}>
                <InputLabel>Documento</InputLabel>
                <Select label="Documento" value={form.documentType} onChange={(e) => setForm((f) => ({ ...f, documentType: e.target.value as any }))}>
                  <MenuItem value="DNI">DNI</MenuItem>
                  <MenuItem value="PASSPORT">Pasaporte</MenuItem>
                </Select>
              </FormControl>
            </Stack>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <DateTimePicker
                label="Válida desde"
                value={form.validFrom}
                onChange={(v) => setForm((f) => ({ ...f, validFrom: v || new Date() }))}
                slotProps={{ textField: { size: 'small', fullWidth: true } }}
              />
              <DateTimePicker
                label="Válida hasta (opcional)"
                value={form.validTo}
                onChange={(v) => setForm((f) => ({ ...f, validTo: v }))}
                slotProps={{ textField: { size: 'small', fullWidth: true } }}
              />
            </Stack>
            <TextField label="Descripción (opcional)" value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} fullWidth multiline rows={2} size="small" />
            <FormControlLabel control={<Switch checked={form.active} onChange={(e) => setForm((f) => ({ ...f, active: e.target.checked }))} />} label="Tarifa activa" />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, flexDirection: { xs: 'column-reverse', sm: 'row' }, gap: 1 }}>
          <Button onClick={() => setOpen(false)} fullWidth={isXs}>Cancelar</Button>
          <Button variant="contained" onClick={handleSave} disabled={busy} fullWidth={isXs}>{busy ? 'Guardando...' : 'Guardar'}</Button>
        </DialogActions>
      </Dialog>
    </PageContainer>
  );
}
